import CT from '@marketplace-ai/tags';
import { ColDef, DoesFilterPassParams, IRowNode } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import { CustomDateRangeFilter } from '~/components/agGrid/filters/CustomDateFilter';
import {
  ACTIONS_COLUMN_BASE,
  ActionsRenderer,
  AiJobStatusCellRenderer,
  type ActionDef,
} from '~/components/Table/gridUtil';
import { NewRecordPill } from '../newRecords/NewRecordPill';
import { isUploadedToday } from './isUploadedToday';
import {
  INSURANCE_CERTIFICATE_UPLOAD,
  PARSE_EXTENSIONS_BY_WORKFLOW,
  canAiParseDocument,
} from './parseWorkflow';

const TAGS = CT.documents.table;

// A row's upload time in ms. A document with no `uploadedAt` yet (one just appended to the grid
// before the server stamps it) falls back to `createdAt`, and with neither it counts as the
// newest: it was uploaded a moment ago, so it belongs at the top of a newest-first sort.
const uploadedAtMs = (value: string | null | undefined, node?: IRowNode): number => {
  const time = Date.parse(value ?? node?.data?.createdAt ?? '');
  return Number.isNaN(time) ? Number.POSITIVE_INFINITY : time;
};

// Exported for the spec. AG Grid hands a custom comparator the raw cell values, nulls
// included, and negates the result itself for a descending sort.
export const compareUploadedAt = (
  a: string | null | undefined,
  b: string | null | undefined,
  nodeA?: IRowNode,
  nodeB?: IRowNode
): number => {
  const timeA = uploadedAtMs(a, nodeA);
  const timeB = uploadedAtMs(b, nodeB);
  if (timeA === timeB) return 0;
  return timeA < timeB ? -1 : 1;
};

interface DocumentColumnParams {
  handleEdit: (id: number) => void;
  handleDelete: (id: number) => void;
  handleDownload: (id: number) => void;
  handleParse: (id: number) => void;
}

// [MCV-1187] Status cell only renders when the document has at least one AI
// job. That already gates display to rows that actually went through parsing,
// so we don't need an additional docType filter — it was also causing the
// "Status" column to read as broken for non-cv/packet rows (column header
// present but cells always empty).
const cannotBeParsed = (params: CustomCellRendererProps) => !params.data;

export const CreateDocumentColumns = ({
  handleEdit,
  handleDelete,
  handleDownload,
  handleParse,
}: DocumentColumnParams): ColDef[] => [
  { field: 'id', hide: true },
  {
    field: 'docName',
    headerName: 'Name',
    tooltipField: 'docName',
    // [MCV-1812] A "New" pill on a document uploaded today (the viewer's local day). Purely
    // client-side: it reads the uploadedAt the list already returns.
    cellRenderer: ({ data, value }: CustomCellRendererProps) => {
      const name = <span className="truncate">{String(value ?? '')}</span>;
      if (!isUploadedToday(data?.uploadedAt)) return name;
      return (
        <span className="flex min-w-0 items-center">
          {name}
          <NewRecordPill />
        </span>
      );
    },
  },
  {
    field: 'uploadedAt',
    headerName: 'Uploaded At',
    cellRenderer: ({ data }: CustomCellRendererProps) =>
      data?.uploadedAt
        ? new Date(data.uploadedAt).toLocaleString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
          })
        : '',
    filter: {
      component: CustomDateRangeFilter,
      doesFilterPass: ({ model, node }: DoesFilterPassParams) => {
        if (!model || (!model.startDate && !model.endDate)) return true;
        if (!node.data?.uploadedAt) return false;

        const nodeDate = new Date(node.data.uploadedAt);
        if (Number.isNaN(nodeDate.getTime())) return false;

        // Parse the `YYYY-MM-DD` filter strings as local-day boundaries.
        // `new Date("2025-04-14")` parses as UTC midnight, which shifts to the
        // previous calendar day in negative UTC offsets and misaligns the
        // filter from what the user sees in their local timezone.
        const parseLocalDay = (ymd: string): Date | null => {
          const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
          if (!match) return null;
          const [, y, m, d] = match;
          return new Date(Number(y), Number(m) - 1, Number(d));
        };

        const startDate = model.startDate ? parseLocalDay(model.startDate) : null;
        const endDate = model.endDate ? parseLocalDay(model.endDate) : null;
        if (endDate) {
          endDate.setDate(endDate.getDate() + 1);
        }
        const nodeTime = nodeDate.getTime();
        if (startDate && nodeTime < startDate.getTime()) return false;
        if (endDate && nodeTime >= endDate.getTime()) return false;
        return true;
      },
    },
    // [MCV-1798] Newest upload first until the user picks another sort. `initialSort` is
    // applied once, so a re-render with a fresh colDefs array does not reset their choice.
    initialSort: 'desc',
    comparator: compareUploadedAt,
  },
  {
    field: 'docDesc',
    headerName: 'Description',
  },
  {
    colId: 'ai-job-status',
    headerName: 'Status',
    cellRenderer: (params: CustomCellRendererProps) => {
      if (cannotBeParsed(params)) {
        return '';
      }
      return AiJobStatusCellRenderer({
        ...params,
        cyTag: TAGS.columns.parseAction,
      });
    },
  },
  {
    ...ACTIONS_COLUMN_BASE,
    width: 140,
    cellRenderer: ActionsRenderer,
    cellRendererParams: {
      actions: [
        {
          // [MCV-1426] Parse only handles CV and packet content. Showing the
          // button on other doc types invited misleading parse runs (a
          // `cv`-flavored run against an unrelated document). Reverses the
          // MCV-1187 "always show" decision — the empty slot beats a broken
          // affordance. CVs, packets and insurance certificates parse on upload when the
          // file is a format their workflow reads (MCV-1761); this action is the manual
          // re-parse, and it is hidden for a file the workflow cannot read (MCV-1760).
          type: 'parse',
          handler: handleParse,
          cyTag: TAGS.columns.parseAction,
          show: canAiParseDocument,
        },
        { type: 'download', handler: handleDownload, cyTag: TAGS.columns.downloadAction },
        {
          type: 'edit',
          handler: handleEdit,
          cyTag: TAGS.columns.editAction,
          // Attestations get the edit button for renaming (title only). The
          // form disables doc type/category for attestations so the row's
          // attestation association can't be stripped off by accident.
        },
        { type: 'delete', handler: handleDelete, cyTag: TAGS.columns.deleteAction },
      ] satisfies ActionDef[],
    },
  },
];

// Max upload size — must match the backend FILE_SIZE_LIMIT
// (apps/mcv-api/mcv_api/routes/document/constants.py). nginx allows 25MB as headroom.
export const MAX_FILE_SIZE_MB = 20;
export const MAX_FILE_SIZE_BYTES = 20_000_000;

// mcv.document_info doc_name and uploaded_file_name are VARCHAR(64); mirrors
// MAX_FILE_NAME_LENGTH in the API's constants.py.
export const MAX_FILE_NAME_LENGTH = 64;

// Order-preserving, de-duplicating concatenation of extension lists.
export const unionOf = (...lists: (readonly string[])[]): string[] =>
  Array.from(new Set(lists.flat()));

// `accept` value for <input type="file"> and the display form for alerts and helper text,
// e.g. "PDF, DOCX, TXT".
export const toAccept = (extensions: readonly string[]): string =>
  extensions.map((ext) => `.${ext}`).join(',');
export const toTypesLabel = (extensions: readonly string[]): string =>
  extensions.map((ext) => ext.toUpperCase()).join(', ');

// The formats the vault stores (MCV-1760 tables): encrypted and kept, not parsed. Mirrors
// STORABLE_EXTENSIONS in apps/mcv-api/mcv_api/routes/document/constants.py. Extensions are the
// authority: browsers report .md, .tsv and .heic with an empty or unusual MIME type, so a MIME
// list rejects or hides files the vault stores. Parsing is a separate, narrower rule per
// workflow (PARSE_EXTENSIONS_BY_WORKFLOW in parseWorkflow.ts).
export const STORABLE_EXTENSIONS = [
  'docx',
  'doc',
  'rtf',
  'pdf',
  'txt',
  'xlsx',
  'xls',
  'csv',
  'tsv',
  'pptx',
  'ppt',
  'jpg',
  'jpeg',
  'png',
  'gif',
  'tif',
  'tiff',
  'webp',
  'svg',
  'heic',
  'heif',
] as const;

// Signup picks the file before it asks CV or Packet, so its gate is either parse list
// (libs/ai-services/ocr reads these; legacy Word `.doc` is converted to `.docx` by the
// doc-convert-service Lambda first).
export const PARSEABLE_EXTENSIONS = unionOf(
  PARSE_EXTENSIONS_BY_WORKFLOW['cv-parse'],
  PARSE_EXTENSIONS_BY_WORKFLOW['packet-parse']
);
export const PARSEABLE_ACCEPT = toAccept(PARSEABLE_EXTENSIONS);
export const PARSEABLE_TYPES_LABEL = toTypesLabel(PARSEABLE_EXTENSIONS);

// Every format some File Type accepts on the Documents page (the stored formats plus every
// parse list, so an .html or .md CV still uploads). The picker's `accept` before a File Type
// is chosen; the gate after is documentsUploadExtensions in utils.ts.
export const DOCUMENTS_PICKER_EXTENSIONS = unionOf(
  STORABLE_EXTENSIONS,
  ...Object.values(PARSE_EXTENSIONS_BY_WORKFLOW)
);
export const DOCUMENTS_PICKER_ACCEPT = toAccept(DOCUMENTS_PICKER_EXTENSIONS);

// Files "Combine into one packet" (MCV-1765) can merge into one PDF: the packet parse list minus
// Word, because the API has no Word-to-PDF converter. Mirrors COMBINABLE_EXTENSIONS in the API's
// constants.py.
export const COMBINABLE_EXTENSIONS = ['pdf', 'png', 'jpg', 'jpeg'] as const;

// Browser-declared types that agree with each extension, for fast feedback before the upload.
// Mirrors DECLARED_CONTENT_TYPES in the API's constants.py, which is the real check (the
// server also sniffs the bytes). '' and application/octet-stream mean "unknown": browsers send
// them for heic, tsv and the like, so they never count as a contradiction.
export const UNKNOWN_DECLARED_TYPES: readonly string[] = ['', 'application/octet-stream'];
export const DECLARED_TYPES_BY_EXTENSION: Record<string, readonly string[]> = {
  pdf: ['application/pdf', 'application/x-pdf'],
  doc: ['application/msword'],
  docx: [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/zip',
    'application/x-zip-compressed',
  ],
  // Windows maps .rtf to msword when Office is installed.
  rtf: ['application/rtf', 'text/rtf', 'application/msword'],
  txt: ['text/plain'],
  // Windows with Excel installed reports .csv as application/vnd.ms-excel.
  csv: [
    'text/csv',
    'text/plain',
    'application/csv',
    'text/x-csv',
    'text/comma-separated-values',
    'application/vnd.ms-excel',
  ],
  tsv: ['text/tab-separated-values', 'text/plain'],
  md: ['text/markdown', 'text/x-markdown', 'text/plain'],
  html: ['text/html'],
  xlsx: [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/zip',
    'application/x-zip-compressed',
  ],
  xls: ['application/vnd.ms-excel'],
  pptx: [
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/zip',
    'application/x-zip-compressed',
  ],
  ppt: ['application/vnd.ms-powerpoint'],
  jpg: ['image/jpeg', 'image/pjpeg'],
  jpeg: ['image/jpeg', 'image/pjpeg'],
  png: ['image/png', 'image/x-png'],
  gif: ['image/gif'],
  tif: ['image/tiff', 'image/x-tiff'],
  tiff: ['image/tiff', 'image/x-tiff'],
  webp: ['image/webp'],
  svg: ['image/svg+xml'],
  heic: ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'],
  heif: ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'],
};

// MCV-1761: the File Type and File Category menus say which options AI parsing applies to,
// keyed by option value. Kept out of DOCUMENT_TYPES so the hint never leaks into form values
// (certificateUpload.machine builds its values from those option objects). Certificates only
// parse in the Malpractice Insurance category (a PDF), hence the two matching hints.
export const DOCUMENT_TYPE_PARSE_HINTS: Record<string, string> = {
  cv: 'Can be parsed',
  packet: 'Can be parsed',
  [INSURANCE_CERTIFICATE_UPLOAD.docType]: 'Can be parsed in Malpractice Insurance only',
};
export const DOCUMENT_CATEGORY_PARSE_HINTS: Record<string, string> = {
  [INSURANCE_CERTIFICATE_UPLOAD.docCategory]: 'Certificates can be parsed here',
};

// MCV-1425 / MCV-1426: type and category dropdowns are alphabetized for
// scanability. Exception: Packet is co-located with CV because both are
// the only doc types the AI parser handles, and users picking one often
// want to compare against the other.
// The category list must stay aligned with the `DocumentCategory` enum in
// libs/models/models/mcv/document_info.py.
export const DOCUMENT_TYPES = [
  { label: 'Attestation', value: 'attestation' },
  { label: 'Card', value: 'card' },
  { label: 'Certificate', value: 'certificate' },
  { label: 'CV', value: 'cv' },
  { label: 'Packet', value: 'packet' },
  { label: 'Diploma', value: 'diploma' },
  { label: 'Form', value: 'form' },
  { label: 'Letter', value: 'letter' },
  { label: 'License', value: 'license' },
  { label: 'Other', value: 'other' },
  { label: 'Transcript', value: 'transcript' },
];

export const DOCUMENT_CATEGORIES = [
  { label: 'Certifications', value: 'certifications' },
  { label: 'Continuing Education', value: 'continuing_education' },
  { label: 'Education & Training', value: 'education_and_training' },
  { label: 'Employment & Work History', value: 'employment_and_work_history' },
  { label: 'Identification & Legal Documents', value: 'identification_and_legal_documents' },
  { label: 'Licensing', value: 'licensing' },
  { label: 'Malpractice Insurance', value: 'malpractice_insurance' },
  { label: 'Other', value: 'other' },
  { label: 'References', value: 'references' },
];
